import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { AlertTriangle, Upload, CheckCircle2, Clock, Send, Building2, User, Image, XCircle } from 'lucide-react';
import { TYPES_DOMMAGES } from './declarationDommageShared';

export const DeclarationNouvelleForm = ({ formData, souscriptions, files, uploadProgress, loading, handleInputChange, handleFileSelect, removeFile, handleSubmit }) => {
  return (
    <>
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <strong>Rappel :</strong> Cette déclaration doit être transmise dans les{' '}
                  <strong>5 jours ouvrés</strong> suivant la découverte des désordres.
                </AlertDescription>
              </Alert>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Section 1: Identification */}
                <fieldset className="border rounded-lg p-4 space-y-4">
                  <legend className="font-semibold text-[#002d5b] px-2">
                    <User className="inline h-4 w-4 mr-2" />
                    1. Identification de l'Adhérent
                  </legend>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="subscription_reference">N° d'Attestation DOS *</Label>
                      <Select
                        value={formData.subscription_reference}
                        onValueChange={(v) => handleInputChange('subscription_reference', v)}
                      >
                        <SelectTrigger data-testid="select-souscription">
                          <SelectValue placeholder="Sélectionner votre souscription" />
                        </SelectTrigger>
                        <SelectContent>
                          {souscriptions.map((s) => (
                            <SelectItem key={s.reference} value={s.reference}>
                              {s.reference} - {s.operation?.ville_chantier || 'N/A'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="nom">Nom / Raison Sociale *</Label>
                      <Input
                        id="nom"
                        value={formData.nom}
                        onChange={(e) => handleInputChange('nom', e.target.value)}
                        required
                        data-testid="input-nom"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email de contact *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        data-testid="input-email"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="telephone">Téléphone *</Label>
                      <Input
                        id="telephone"
                        value={formData.telephone}
                        onChange={(e) => handleInputChange('telephone', e.target.value)}
                        required
                        data-testid="input-telephone"
                      />
                    </div>
                  </div>
                </fieldset>
                
                {/* Section 2: Informations sur le dommage */}
                <fieldset className="border rounded-lg p-4 space-y-4">
                  <legend className="font-semibold text-[#002d5b] px-2">
                    <Building2 className="inline h-4 w-4 mr-2" />
                    2. Informations sur le Dommage
                  </legend>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date_decouverte">Date de découverte des désordres *</Label>
                      <DatePickerFr
                        id="date_decouverte"
                        className="w-full mt-1"
                        value={formData.date_decouverte}
                        onChange={(v) => handleInputChange('date_decouverte', v)}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="type_dommage">Nature du désordre *</Label>
                      <Select
                        value={formData.type_dommage}
                        onValueChange={(v) => handleInputChange('type_dommage', v)}
                      >
                        <SelectTrigger data-testid="select-type-dommage">
                          <SelectValue placeholder="Sélectionner le type" />
                        </SelectTrigger>
                        <SelectContent>
                          {TYPES_DOMMAGES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="localisation">Localisation des dégâts</Label>
                    <Input
                      id="localisation"
                      value={formData.localisation}
                      onChange={(e) => handleInputChange('localisation', e.target.value)}
                      placeholder="Ex: Mur nord, façade principale, sous-sol..."
                      data-testid="input-localisation"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description détaillée *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={5}
                      placeholder="Décrivez précisément l'emplacement et l'évolution du dommage..."
                      required
                      data-testid="input-description"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="evolution">Évolution constatée</Label>
                    <Textarea
                      id="evolution"
                      value={formData.evolution}
                      onChange={(e) => handleInputChange('evolution', e.target.value)}
                      rows={2}
                      placeholder="Comment le dommage a-t-il évolué depuis sa découverte?"
                      data-testid="input-evolution"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="urgence"
                      checked={formData.urgence}
                      onCheckedChange={(checked) => handleInputChange('urgence', checked)}
                      data-testid="checkbox-urgence"
                    />
                    <Label htmlFor="urgence" className="text-red-600 font-medium">
                      ⚠️ Situation urgente (risque immédiat pour la sécurité ou l'intégrité de l'ouvrage)
                    </Label>
                  </div>
                </fieldset>
                
                {/* Section 3: Justificatifs */}
                <fieldset className="border rounded-lg p-4 space-y-4">
                  <legend className="font-semibold text-[#002d5b] px-2">
                    <Image className="inline h-4 w-4 mr-2" />
                    3. Justificatifs & Photos (Convention de Preuve)
                  </legend>
                  
                  <p className="text-sm text-gray-600">
                    Veuillez joindre au minimum 3 photos : une vue d'ensemble et deux vues rapprochées du désordre.
                  </p>
                  
                  <div 
                    className="border-2 border-dashed border-[#002d5b] rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => document.getElementById('file-upload').click()}
                    data-testid="file-upload-zone"
                  >
                    <Upload className="h-10 w-10 mx-auto text-[#002d5b] mb-2" />
                    <p className="text-[#002d5b] font-medium">Cliquez ou glissez vos fichiers ici</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Photos, PV de réception, Devis de réparation (JPG, PNG, PDF - max 10MB)
                    </p>
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </div>
                  
                  {files.length > 0 && (
                    <div className="space-y-2">
                      <p className="font-medium text-sm">{files.length} fichier(s) sélectionné(s) :</p>
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm truncate flex-1">{file.name}</span>
                          <span className="text-xs text-gray-500 mx-2">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                          {uploadProgress[index] === 'success' && (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                          {uploadProgress[index] === 'error' && (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          {!uploadProgress[index] && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(index)}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </fieldset>
                
                {/* Certification RGPD */}
                <div className="bg-gray-50 border rounded-lg p-4 space-y-4">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="certifie"
                      checked={formData.certifie}
                      onCheckedChange={(checked) => handleInputChange('certifie', checked)}
                      required
                      data-testid="checkbox-certifie"
                    />
                    <Label htmlFor="certifie" className="text-sm leading-relaxed">
                      Je certifie l'exactitude des informations fournies. Je reconnais que toute 
                      altération frauduleuse de document numérique entraîne l'exclusion du 
                      dispositif (Art. 12).
                    </Label>
                  </div>
                  
                  <hr />
                  
                  <p className="text-xs text-gray-600">
                    <strong>Note RGPD :</strong> Les données collectées sont traitées par COPPAM / F.O.G.E.D.O.M 
                    pour l'instruction de votre dossier par la Cellule d'Expertise Préventive. Elles sont 
                    conservées 10 ans. Conformément au règlement, vous pouvez exercer votre droit d'accès à :{' '}
                    <em>dpo@coppam-garanties-solidaires.fr</em>
                  </p>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-[#002d5b] hover:bg-[#004085] text-white py-6 text-lg font-bold"
                  disabled={loading || !formData.certifie}
                  data-testid="btn-submit-declaration"
                >
                  {loading ? (
                    <>
                      <Clock className="h-5 w-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      TRANSMETTRE LA DÉCLARATION AU COMITÉ
                    </>
                  )}
                </Button>
              </form>
    </>
  );
};
